import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Plus, Edit2, Eye, EyeOff, ChevronDown, BookOpen } from 'lucide-react';
import { listRestaurants, createRestaurant, getResortLocations, listFoodTags, getRestaurantTypeOptions, publishRestaurant, unpublishRestaurant, uploadFile, updateRestaurant, updateRestaurantsOrder } from '../services/resortAdminApi';
import { TimetableEditor } from '../components/TimetableEditor';
import { DeliveryTypesSection, DeliveryTypesConfig } from '../components/DeliveryTypesSection';
import { useAuth } from '../context/AuthContext';
import { validateCoordinates } from '../utils/coordinateValidation';

const RESTAURANT_TYPE_MAX = 3;

const parseRestaurantTypes = (raw: string | undefined | null): string[] =>
  (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const EMPTY_DELIVERY_CONFIG: DeliveryTypesConfig = {
  delivery_enabled: 0,
  click_and_collect: 0,
  delivery_scheduling_enabled: 0,
  click_and_collect_scheduling_enabled: 0,
  delivery_schedule: null,
  click_and_collect_schedule: null,
  delivery_scheduled_config: null,
  click_and_collect_scheduled_config: null,
};

interface Restaurant {
  name: string;
  restaurant_name: string;
  restaurant_code?: string;
  resort_location: string;
  is_active: number;
  is_published: number;
}

export const Restaurants: React.FC = () => {
  const navigate = useNavigate();
  const { user, isReadOnly } = useAuth();
  const isSuperAdmin = user?.role === 'Resort Super Admin' || user?.role === 'Supervisor';
  const [searchTerm, setSearchTerm] = useState('');
  const [resortFilter, setResortFilter] = useState('');
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [resortLocations, setResortLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [restaurantTypeOptions, setRestaurantTypeOptions] = useState<string[]>([]);
  const [, setAvailableFoodTags] = useState<Array<{ name: string; food_tag_name: string }>>([]);
  const [foodTypeTags, setFoodTypeTags] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    resort_location: '',
    restaurant_name: '',
    restaurant_code: '',
    restaurant_description: '',
    restaurant_coordinates: '',
    restaurant_type: '',
    restaurant_address: '',
    est_delivery_time: 50,
    pricing_tier: '',
    min_order_value: '' as string | number,
  });
  const [timetable, setTimetable] = useState<{
    weekly: Record<string, { periods: Array<{ start: string; end: string }>; non_business: boolean; is_24_7?: boolean }>;
    special_days: Array<{
      start_date: string;
      end_date: string;
      periods: Array<{ start: string; end: string }>;
      non_business: boolean;
    }>;
  }>({
    weekly: {
      monday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      tuesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      wednesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      thursday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      friday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      saturday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
      sunday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
    },
    special_days: [],
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errorModal, setErrorModal] = useState<{ show: boolean; title: string; errors: string[] }>({ show: false, title: '', errors: [] });
  const createErrorRef = useRef<HTMLDivElement>(null);
  // Surface validation errors inside the create modal (the page-level banner sits behind the overlay).
  useEffect(() => {
    if (error && showCreateModal) {
      createErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, showCreateModal]);
  const [createDeliveryConfig, setCreateDeliveryConfig] = useState<DeliveryTypesConfig>(EMPTY_DELIVERY_CONFIG);
  const [showDeliveryTypeDropdown, setShowDeliveryTypeDropdown] = useState(false);

  const restaurantTypeSelectOptions = useMemo(
    () => restaurantTypeOptions.map((opt) => ({ value: opt, label: opt })),
    [restaurantTypeOptions],
  );

  const selectedRestaurantTypeOptions = useMemo(
    () => parseRestaurantTypes(formData.restaurant_type).map((value) => ({ value, label: value })),
    [formData.restaurant_type],
  );

  const openCreateModal = (preset: keyof Pick<DeliveryTypesConfig,
    'delivery_enabled' | 'delivery_scheduling_enabled' | 'click_and_collect' | 'click_and_collect_scheduling_enabled'>) => {
    setCreateDeliveryConfig({ ...EMPTY_DELIVERY_CONFIG, [preset]: 1 });
    setShowDeliveryTypeDropdown(false);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateDeliveryConfig(EMPTY_DELIVERY_CONFIG);
  };

  // Load resort locations
  useEffect(() => {
    const loadResortLocations = async () => {
      try {
        const result = await getResortLocations();
        if (result && result.resort_locations) {
          setResortLocations(result.resort_locations);
        }
      } catch (error) {
        console.error('Error loading resort locations:', error);
      }
    };
    loadResortLocations();
  }, []);

  // Load restaurant type options
  useEffect(() => {
    const loadRestaurantTypeOptions = async () => {
      try {
        const options = await getRestaurantTypeOptions();
        setRestaurantTypeOptions(options || []);
      } catch (error) {
        console.error('Error loading restaurant type options:', error);
      }
    };
    loadRestaurantTypeOptions();
  }, []);

  // Load food tags
  useEffect(() => {
    const loadFoodTags = async () => {
      try {
        const tags = await listFoodTags(true); // Only get tags with is_restaurant_tag=true
        setAvailableFoodTags(tags || []);
      } catch (error) {
        console.error('Error loading food tags:', error);
      }
    };
    loadFoodTags();
  }, []);

  // Load restaurants from API
  const loadRestaurants = async () => {
    setLoading(true);
    try {
      const result = await listRestaurants(searchTerm || undefined, page, pageSize, resortFilter || undefined);
      if (result && result.data) {
        setRestaurants(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRestaurants();
  }, [page, pageSize, searchTerm, resortFilter]);

  // Reordering is only meaningful inside a single resort's list, without a
  // search narrowing it down. The saved order drives the client app.
  const canReorder = isSuperAdmin && !isReadOnly && !!resortFilter && !searchTerm;

  const handleRowDrop = async (fromIndex: number, toIndex: number) => {
    const next = [...restaurants];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setRestaurants(next); // optimistic
    try {
      const offset = (page - 1) * pageSize;
      await updateRestaurantsOrder(next.map((r, i) => ({ name: r.name, order: offset + i + 1 })));
    } catch (error: any) {
      setError(error.message || 'Failed to save restaurant order');
      loadRestaurants();
    }
  };

  const handlePublishToggle = async (restaurant: Restaurant) => {
    setError('');
    setSuccess('');
    try {
      if (restaurant.is_published) {
        const result = await unpublishRestaurant(restaurant.name);
        if (result.ok === false) {
          setErrorModal({ show: true, title: 'Cannot Unpublish Restaurant', errors: result.errors || [result.error || 'Unknown error'] });
          return;
        }
        setSuccess(`Restaurant "${restaurant.restaurant_name}" has been unpublished`);
      } else {
        const result = await publishRestaurant(restaurant.name);
        if (result.ok === false) {
          setErrorModal({ show: true, title: 'Cannot Publish Restaurant', errors: result.errors || [result.error || 'Unknown error'] });
          return;
        }
        setSuccess(`Restaurant "${restaurant.restaurant_name}" has been published`);
      }
      loadRestaurants();
    } catch (error: any) {
      setError(error.message || 'Failed to update publish status');
    }
  };

  // Table columns
  const columns = [
    {
      key: 'restaurant_name',
      header: 'Restaurant Name'
    },
    {
      key: 'restaurant_code',
      header: 'Code',
      render: (value: any) => value || '-'
    },
    {
      key: 'resort_location',
      header: 'Resort Location'
    },
    {
      key: 'is_published',
      header: 'Published',
      render: (value: any) => (
        <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
          {value ? 'Published' : 'Draft'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: Restaurant) => (
        <div className="flex space-x-2">
          {isSuperAdmin && (
            <button
              onClick={() => handlePublishToggle(row)}
              disabled={isReadOnly}
              className={`${row.is_published ? 'text-green-600 hover:text-green-900' : 'text-red-600 hover:text-red-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
              title={row.is_published ? 'Unpublish' : 'Publish'}
            >
              {row.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          )}
          <button
            onClick={() => handleEdit(row)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => navigate(`/restaurants/${row.name}/menu`)}
            className="text-emerald-600 hover:text-emerald-900"
            title="Edit menu"
          >
            <BookOpen className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const handleEdit = (restaurant: Restaurant) => {
    navigate(`/restaurants/${restaurant.name}`);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate required fields
    if (!formData.resort_location || !formData.restaurant_name) {
      setError('Resort location and restaurant name are required');
      return;
    }
    if (!formData.restaurant_description?.trim()) {
      setError('Restaurant Description is required');
      return;
    }
    const coordsError = validateCoordinates(formData.restaurant_coordinates || '');
    if (coordsError) {
      setError(coordsError);
      return;
    }
    if (!formData.restaurant_type?.trim()) {
      setError('Restaurant Type is required');
      return;
    }
    if (!formData.restaurant_address?.trim()) {
      setError('Restaurant Address is required');
      return;
    }
    if (!formData.pricing_tier) {
      setError('Pricing Tier is required');
      return;
    }
    if (!timetable) {
      setError('Working Timetable is required');
      return;
    }

    if (
      !createDeliveryConfig.delivery_enabled
      && !createDeliveryConfig.delivery_scheduling_enabled
      && !createDeliveryConfig.click_and_collect
      && !createDeliveryConfig.click_and_collect_scheduling_enabled
    ) {
      setError('At least one delivery option must be enabled');
      return;
    }

    if (
      (createDeliveryConfig.delivery_enabled || createDeliveryConfig.delivery_scheduling_enabled)
      && (!formData.est_delivery_time || formData.est_delivery_time < 30)
    ) {
      setError('Estimated Delivery Time must be at least 30 minutes');
      return;
    }

    try {
      const result = await createRestaurant({
        resort_location: formData.resort_location,
        restaurant_name: formData.restaurant_name,
        restaurant_description: formData.restaurant_description,
        restaurant_coordinates: formData.restaurant_coordinates,
        restaurant_type: formData.restaurant_type,
        restaurant_address: formData.restaurant_address,
        working_timetable: timetable,
        restaurant_code: formData.restaurant_code || undefined,
        food_type_tags: foodTypeTags.length > 0 ? foodTypeTags : undefined,
        est_delivery_time: formData.est_delivery_time,
        click_and_collect: createDeliveryConfig.click_and_collect,
        delivery_enabled: createDeliveryConfig.delivery_enabled,
        delivery_scheduling_enabled: createDeliveryConfig.delivery_scheduling_enabled,
        click_and_collect_scheduling_enabled: createDeliveryConfig.click_and_collect_scheduling_enabled,
        delivery_schedule: createDeliveryConfig.delivery_schedule || undefined,
        click_and_collect_schedule: createDeliveryConfig.click_and_collect_schedule || undefined,
        delivery_scheduled_config: createDeliveryConfig.delivery_scheduled_config || undefined,
        click_and_collect_scheduled_config: createDeliveryConfig.click_and_collect_scheduled_config || undefined,
        pricing_tier: formData.pricing_tier,
        min_order_value: formData.min_order_value !== '' ? Number(formData.min_order_value) : undefined,
      });

      // Upload image if selected
      if (imageFile && result?.name) {
        try {
          const uploadResult = await uploadFile(imageFile, result.name);
          if (uploadResult.file_url) {
            await updateRestaurant({
              restaurant_id: result.name,
              restaurant_image: uploadResult.file_url,
            });
          }
        } catch (imgErr: any) {
          console.error('Image upload failed:', imgErr);
        }
      }

      setSuccess('Restaurant created successfully');
      setShowCreateModal(false);
      setCreateDeliveryConfig(EMPTY_DELIVERY_CONFIG);
      setFormData({
        resort_location: '',
        restaurant_name: '',
        restaurant_code: '',
        restaurant_description: '',
        restaurant_coordinates: '',
        restaurant_type: '',
        restaurant_address: '',
        est_delivery_time: 50,
        pricing_tier: '',
        min_order_value: '',
      });
      setImageFile(null);
      setImagePreview(null);
      setFoodTypeTags([]);
      setTimetable({
        weekly: {
          monday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          tuesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          wednesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          thursday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          friday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          saturday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
          sunday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false, is_24_7: false },
        },
        special_days: [],
      });
      loadRestaurants();
    } catch (error: any) {
      setError(error.message || 'Failed to create restaurant');
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Restaurants</h1>
          <p className="mt-1 text-sm text-gray-600">Manage restaurants</p>
        </div>
        {isSuperAdmin && (
          <div className="relative">
            <button
              onClick={() => !isReadOnly && setShowDeliveryTypeDropdown(!showDeliveryTypeDropdown)}
              disabled={isReadOnly}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Restaurant
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>
            {showDeliveryTypeDropdown && (
              <>
              <div className="fixed inset-0 z-[9]" onClick={() => setShowDeliveryTypeDropdown(false)} />
              <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  <button
                    onClick={() => openCreateModal('delivery_enabled')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Delivery
                  </button>
                  <button
                    onClick={() => openCreateModal('delivery_scheduling_enabled')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Delivery — Scheduled
                  </button>
                  <button
                    onClick={() => openCreateModal('click_and_collect')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Click & Collect
                  </button>
                  <button
                    onClick={() => openCreateModal('click_and_collect_scheduling_enabled')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Click & Collect — Scheduled
                  </button>
                </div>
              </div>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <Card>
        <SearchFilter
          placeholder="Search restaurants..."
          onSearch={setSearchTerm}
          initialValue={searchTerm}
        />
        <div className="mb-4 flex items-center space-x-3">
          <label htmlFor="resort-filter" className="text-sm font-medium text-gray-700">Resort:</label>
          <select
            id="resort-filter"
            value={resortFilter}
            onChange={(e) => { setResortFilter(e.target.value); setPage(1); }}
            className="block w-64 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
          >
            <option value="">All resorts</option>
            {resortLocations.map((loc) => (
              <option key={loc.value} value={loc.value}>{loc.label}</option>
            ))}
          </select>
          {canReorder && (
            <span className="text-sm text-gray-500">
              Drag rows to set the order shown in the client app
            </span>
          )}
        </div>
        <Table
          columns={columns}
          data={restaurants}
          isLoading={loading}
          emptyMessage="No restaurants found"
          pagination={{
            page,
            page_size: pageSize,
            total
          }}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRowDrop={canReorder ? handleRowDrop : undefined}
        />
      </Card>

      {/* Create Restaurant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Create Restaurant</h3>
              <button onClick={closeCreateModal} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} noValidate className="space-y-4">
              {error && (
                <div ref={createErrorRef} className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Restaurant Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.restaurant_name}
                    onChange={(e) => setFormData({ ...formData, restaurant_name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Resort Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.resort_location}
                    onChange={(e) => setFormData({ ...formData, resort_location: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="">Select resort location</option>
                    {resortLocations.map((loc) => (
                      <option key={loc.value} value={loc.value}>
                        {loc.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Restaurant Type <span className="text-red-500">*</span>
                    <span className="ml-2 font-normal text-xs text-gray-500">(up to {RESTAURANT_TYPE_MAX})</span>
                  </label>
                  <Select
                    isMulti
                    options={restaurantTypeSelectOptions}
                    value={selectedRestaurantTypeOptions}
                    isOptionDisabled={() => selectedRestaurantTypeOptions.length >= RESTAURANT_TYPE_MAX}
                    onChange={(selected) => {
                      const next = (selected ?? [])
                        .slice(0, RESTAURANT_TYPE_MAX)
                        .map((opt) => opt.value);
                      setFormData({ ...formData, restaurant_type: next.join(', ') });
                    }}
                    placeholder="Select restaurant type"
                    className="mt-1"
                    classNamePrefix="rs"
                    noOptionsMessage={() =>
                      selectedRestaurantTypeOptions.length >= RESTAURANT_TYPE_MAX
                        ? `Maximum ${RESTAURANT_TYPE_MAX} types reached — remove one to add another`
                        : 'No options'
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Restaurant Code (optional)</label>
                  <input
                    type="text"
                    value={formData.restaurant_code}
                    onChange={(e) => setFormData({ ...formData, restaurant_code: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Pricing Tier <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.pricing_tier}
                    onChange={(e) => setFormData({ ...formData, pricing_tier: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="">Select pricing tier</option>
                    <option value="€">€</option>
                    <option value="€€">€€</option>
                    <option value="€€€">€€€</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Types</label>
                <DeliveryTypesSection
                  config={createDeliveryConfig}
                  onChange={setCreateDeliveryConfig}
                  workingTimetable={timetable}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Restaurant Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.restaurant_description}
                  onChange={(e) => setFormData({ ...formData, restaurant_description: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  placeholder="Describe the restaurant..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Restaurant Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.restaurant_address}
                  onChange={(e) => setFormData({ ...formData, restaurant_address: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  placeholder="Enter restaurant address..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Restaurant Coordinates <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.restaurant_coordinates}
                  onChange={(e) => setFormData({ ...formData, restaurant_coordinates: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  placeholder="e.g., 45.8992, 6.1294"
                />
              </div>

              {(!!createDeliveryConfig.delivery_enabled || !!createDeliveryConfig.delivery_scheduling_enabled) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Estimated Delivery Time, min <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={30}
                    required
                    value={formData.est_delivery_time}
                    onChange={(e) => setFormData({ ...formData, est_delivery_time: parseInt(e.target.value) || 50 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Minimum 30 minutes</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Minimum Order Value, &euro; (optional)
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.min_order_value}
                  onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  placeholder="e.g., 15.00"
                />
                <p className="mt-1 text-xs text-gray-500">Leave empty for no minimum</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Restaurant Image (optional)
                </label>
                {imagePreview ? (
                  <div className="mt-2 space-y-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-md border border-gray-300"
                    />
                    <div className="flex gap-2">
                      <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                        Change Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
                              if (file.size > 5 * 1024 * 1024) { setError('Image size must be less than 5MB'); return; }
                              setImageFile(file);
                              setImagePreview(URL.createObjectURL(file));
                            }
                          }}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => { setImageFile(null); setImagePreview(null); }}
                        className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                      Select Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
                            if (file.size > 5 * 1024 * 1024) { setError('Image size must be less than 5MB'); return; }
                            setImageFile(file);
                            setImagePreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Working Timetable <span className="text-red-500">*</span>
                </label>
                <TimetableEditor
                  timetable={timetable}
                  onChange={(updatedTimetable) => setTimetable(updatedTimetable)}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-red-600">{errorModal.title}</h3>
              <button onClick={() => setErrorModal({ show: false, title: '', errors: [] })} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">The following requirements are not met:</p>
              <ul className="list-disc list-inside space-y-1">
                {errorModal.errors.map((err, index) => (
                  <li key={index} className="text-sm text-red-700">{err}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setErrorModal({ show: false, title: '', errors: [] })}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

